import React from "react";
import { User, Calendar, Clock, ArrowRight, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";



function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="11747" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-border bg-card shadow-sm transition-shadow hover:shadow-md rounded-lg">
          <div className="relative w-full">
            <AspectRatio ratio={16 / 9}>
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-sm font-default font-medium">Image Pending</span>
                </div>
              )}
            </AspectRatio>
          </div>
          <CardHeader className="pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-default text-secondary-foreground">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Latest Update"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-default font-bold leading-tight text-primary">
              <Link to={post.link || "#"} className="hover:text-accent-foreground transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <CardDescription className="line-clamp-3 text-sm font-default text-secondary-foreground">
              {post.excerpt ? (
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Discover more about our specialized medical care and patient resources..."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group rounded-full border-primary text-primary hover:bg-primary hover:text-white">
                Read Full Guide
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId?: string }) {
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    orderby: "date",
    order: "desc",
    ignore_sticky: true,
    exclude: currentPostId ? [parseInt(currentPostId)] : [],
  });

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-default font-bold tracking-tight text-primary md:text-3xl">
          Patient Resources
        </h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex rounded-full text-primary hover:text-accent-foreground">
            View all articles <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsList />
      </PostsProvider>
      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="#">
          <Button variant="outline" className="rounded-full border-primary text-primary">View all articles</Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 px-4 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted" />
        <div className="h-6 w-1/3 animate-pulse rounded-lg bg-muted" />
        <div className="aspect-video w-full animate-pulse rounded-lg bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-default font-bold text-primary">Article not found</h2>
        <p className="mt-2 font-default text-secondary-foreground">The medical guide you are looking for is currently unavailable or has been moved.</p>
        <Link to="#" className="mt-6">
          <Button className="rounded-full bg-primary hover:bg-primary/90">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      
      <header className="mb-10 text-center md:mb-14">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-sm font-default font-medium text-secondary-foreground">
          {post.categories && post.categories.length > 0 && (
            <span className="rounded-full bg-accent/20 px-3 py-1 text-accent-foreground">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : "Medical Insight"}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            Medical Guide
          </span>
        </div>

        <h1 className="mb-6 text-3xl font-default font-bold leading-tight tracking-tight text-primary sm:text-4xl md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        {post.excerpt && (
          <div 
            className="mx-auto max-w-2xl text-lg font-default text-secondary-foreground md:text-xl"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}
      </header>

      
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-lg md:mb-16">
          <AspectRatio ratio={21 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg dark:prose-invert max-w-none 
            [&>p]:mb-6 [&>p]:leading-relaxed [&>p]:text-secondary-foreground [&>p]:font-default
            [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:text-primary [&>h2]:tracking-tight
            [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-semibold [&>h3]:text-primary
            [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-secondary-foreground [&>ul]:font-default
            [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:text-secondary-foreground [&>ol]:font-default
            [&>li]:mb-2
            [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-primary [&>blockquote]:my-8 [&>blockquote]:font-default
            [&>img]:rounded-lg [&>img]:shadow-sm [&>img]:my-8
            [&>a]:text-accent-foreground [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary font-default"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        <Separator className="my-12 border-border" />

        
        <div className="flex flex-col items-center gap-6 rounded-lg bg-muted/40 p-8 text-center sm:flex-row sm:text-left shadow-sm border border-border">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-background shadow-sm">
            {post.author?.avatar ? (
               <img 
                 src={Object.values(post.author.avatar)[0] || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626927/img/generic_avatar.png"} 
                 alt={post.author.name}
                 className="h-full w-full object-cover"
               />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary">
                <User className="h-8 w-8" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="mb-1 text-sm font-default font-medium text-secondary-foreground uppercase tracking-wider">Medical Expert</div>
            <h3 className="mb-2 text-xl font-default font-bold text-primary">
              {post.author?.name || "Clinic Agatas Specialist"}
            </h3>
            <p className="text-secondary-foreground font-default">
              {post.author?.description || "Our medical team at Clinic Agatas provides specialized insights into spine health and advanced pain management protocols for patients from Ireland and across Europe."}
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
             <Button variant="outline" size="sm" className="gap-2 rounded-full border-primary text-primary hover:bg-primary hover:text-white">
                <Share2 className="h-4 w-4" /> Share
             </Button>
          </div>
        </div>

        
        <RelatedPostsSection currentPostId={post.id} />
      </div>
    </article>
  );
}

export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = typeof wvcClient !== 'undefined' ? wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}